import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowUp, ArrowDown, ArrowUpDown, Pencil } from "lucide-react";
import { fmtNum, fmtInr, fmtPct, pnlClass } from "../lib/format";

/**
 * Broker-style holdings grid, inspired by Zerodha/Angel One:
 *  - Symbol left, right-aligned numeric columns
 *  - Sortable headers (Symbol, Day %, LTP, Invested, Mkt Val, P&L, Overall %)
 *  - Sticky first column on scroll (mobile-safe via horizontal scroll)
 */

const COLS = [
  { key: "symbol", label: "Instrument", align: "left", sortable: true, get: (h) => (h.symbol || "").toLowerCase() },
  { key: "quantity", label: "Qty", align: "right", sortable: true, get: (h) => Number(h.quantity) || 0 },
  { key: "avg_cost", label: "Avg. cost", align: "right", sortable: true, get: (h) => Number(h.avg_cost) || 0 },
  { key: "ltp", label: "LTP", align: "right", sortable: true, get: (h) => (h.ltp != null ? Number(h.ltp) : -Infinity) },
  { key: "day_change_pct", label: "Day chg.", align: "right", sortable: true, get: (h) => (h.day_change_pct != null ? Number(h.day_change_pct) : -Infinity) },
  { key: "invested", label: "Invested", align: "right", sortable: true, get: (h) => Number(h.invested) || 0 },
  { key: "market_value", label: "Cur. val", align: "right", sortable: true, get: (h) => (h.market_value != null ? Number(h.market_value) : -Infinity) },
  { key: "unrealized_pnl", label: "P&L", align: "right", sortable: true, get: (h) => (h.unrealized_pnl != null ? Number(h.unrealized_pnl) : -Infinity) },
  { key: "change_pct", label: "Net chg.", align: "right", sortable: true, get: (h) => (h.change_pct != null ? Number(h.change_pct) : -Infinity) },
];

const SORT_OPTIONS = [
  { key: "market_value", label: "Cur. value", dir: "desc" },
  { key: "symbol", label: "Name (A → Z)", dir: "asc" },
  { key: "symbol", label: "Name (Z → A)", dir: "desc" },
  { key: "day_change_pct", label: "Day % ↑", dir: "asc" },
  { key: "day_change_pct", label: "Day % ↓", dir: "desc" },
  { key: "change_pct", label: "Overall % ↑", dir: "asc" },
  { key: "change_pct", label: "Overall % ↓", dir: "desc" },
  { key: "unrealized_pnl", label: "P&L ↓", dir: "desc" },
];

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" strokeWidth={2} />;
  return dir === "asc"
    ? <ArrowUp className="h-3 w-3 text-lime" strokeWidth={2.5} />
    : <ArrowDown className="h-3 w-3 text-lime" strokeWidth={2.5} />;
}

function SymbolCell({ symbol, name }) {
  return (
    <Link
      to={`/stocks/${encodeURIComponent(symbol)}`}
      data-testid={`holding-link-${symbol}`}
      className="group inline-flex flex-col min-w-0"
    >
      <div className="inline-flex items-center gap-1 font-mono text-[13px] font-semibold text-foreground group-hover:text-lime transition-colors">
        {symbol}
        <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
      </div>
      {name ? <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{name}</div> : null}
    </Link>
  );
}

export default function HoldingsTable({ holdings, onEditAvgCost }) {
  const [sortKey, setSortKey] = useState("market_value");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const col = COLS.find((c) => c.key === sortKey);
    const arr = [...(holdings || [])];
    if (!col) return arr;
    arr.sort((a, b) => {
      const va = col.get(a);
      const vb = col.get(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [holdings, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Default direction: text asc, numeric desc
      setSortDir(key === "symbol" ? "asc" : "desc");
    }
  };

  if (!holdings || holdings.length === 0) {
    return (
      <div className="card-noir p-12 text-center text-sm text-muted-foreground rounded-lg">
        No open holdings yet. Record a BUY transaction to begin.
      </div>
    );
  }

  const activeSortLabel = SORT_OPTIONS.find(
    (o) => o.key === sortKey && o.dir === sortDir
  )?.label;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
          {sorted.length} {sorted.length === 1 ? "instrument" : "instruments"}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono hidden sm:block">
            Sort by
          </label>
          <select
            data-testid="holdings-sort-select"
            value={`${sortKey}|${sortDir}`}
            onChange={(e) => {
              const [k, d] = e.target.value.split("|");
              setSortKey(k);
              setSortDir(d);
            }}
            className="border border-border bg-card px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring rounded-md"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={`${o.key}|${o.dir}`} value={`${o.key}|${o.dir}`}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 sm:hidden" data-testid="holdings-cards">
        {sorted.map((h) => (
          <Link
            key={h.symbol}
            to={`/stocks/${encodeURIComponent(h.symbol)}`}
            className="card-noir block p-4 rounded-lg"
            data-testid={`holding-card-${h.symbol}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-bold">{h.symbol}</div>
                {h.name ? <div className="text-[11px] text-muted-foreground truncate">{h.name}</div> : null}
              </div>
              <div className="text-right">
                <div className={`text-base font-mono font-semibold tabular ${pnlClass(h.change_pct)}`}>
                  {h.change_pct != null ? fmtPct(h.change_pct) : "—"}
                </div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground font-mono">Net chg.</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              {[
                ["Qty", fmtNum(h.quantity, 2)],
                ["Avg", fmtNum(h.avg_cost)],
                ["LTP", h.ltp != null ? fmtNum(h.ltp) : "—"],
                ["Day %", h.day_change_pct != null ? fmtPct(h.day_change_pct) : "—", pnlClass(h.day_change_pct)],
                ["Cur. val", h.market_value != null ? fmtInr(h.market_value) : "—"],
                ["P&L", h.unrealized_pnl != null ? fmtInr(h.unrealized_pnl) : "—", pnlClass(h.unrealized_pnl)],
              ].map(([k, v, cls], i) => (
                <div key={`${k}-${i}`}>
                  <div className="text-muted-foreground uppercase tracking-[0.18em] text-[9px] font-mono">{k}</div>
                  <div className={`font-mono tabular text-foreground ${cls || ""}`}>{v}</div>
                </div>
              ))}
            </div>
          </Link>
        ))}
      </div>

      {/* Desktop: broker-style table */}
      <div className="hidden sm:block card-noir overflow-x-auto rounded-lg">
        <table className="w-full text-[13px]" data-testid="holdings-table">
          <thead className="border-b border-border bg-secondary/40">
            <tr className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-mono">
              {COLS.map((c) => {
                const active = sortKey === c.key;
                return (
                  <th
                    key={c.key}
                    data-testid={`holdings-sort-${c.key}`}
                    onClick={() => c.sortable && toggleSort(c.key)}
                    className={`p-3 font-semibold select-none ${
                      c.align === "right" ? "text-right" : "text-left"
                    } ${c.sortable ? "cursor-pointer hover:text-foreground" : ""}`}
                  >
                    <span className={`inline-flex items-center gap-1.5 ${c.align === "right" ? "justify-end" : ""}`}>
                      {c.label}
                      {c.sortable ? <SortIcon active={active} dir={sortDir} /> : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((h, idx) => (
              <tr
                key={h.symbol}
                data-testid={`holding-row-${h.symbol}`}
                className={`border-b border-border/70 last:border-b-0 hover:bg-secondary/60 transition-colors ${
                  idx % 2 === 1 ? "bg-secondary/20" : ""
                }`}
              >
                <td className="p-3">
                  <SymbolCell symbol={h.symbol} name={h.name} />
                </td>
                <td className="p-3 text-right font-mono tabular">{fmtNum(h.quantity, 2)}</td>
                <td className="p-3 text-right font-mono tabular">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    <span className={h.avg_cost_override ? "text-lime" : ""}>
                      {fmtNum(h.avg_cost)}
                    </span>
                    {h.avg_cost_override ? (
                      <span
                        title={`Manual override · auto: ${fmtNum(h.computed_avg_cost)}`}
                        className="text-[8px] uppercase tracking-[0.18em] font-bold text-lime border border-lime/40 bg-lime/10 px-1 rounded"
                        data-testid={`avg-override-badge-${h.symbol}`}
                      >
                        Set
                      </span>
                    ) : null}
                    {onEditAvgCost ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditAvgCost(h);
                        }}
                        data-testid={`edit-avg-cost-${h.symbol}`}
                        className="opacity-40 hover:opacity-100 hover:text-lime transition-opacity"
                        title="Edit average cost"
                      >
                        <Pencil className="h-3 w-3" strokeWidth={2} />
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="p-3 text-right font-mono tabular">{h.ltp != null ? fmtNum(h.ltp) : "—"}</td>
                <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(h.day_change_pct)}`}>
                  {h.day_change_pct != null ? fmtPct(h.day_change_pct) : "—"}
                </td>
                <td className="p-3 text-right font-mono tabular">{fmtInr(h.invested)}</td>
                <td className="p-3 text-right font-mono tabular font-semibold">
                  {h.market_value != null ? fmtInr(h.market_value) : "—"}
                </td>
                <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(h.unrealized_pnl)}`}>
                  {h.unrealized_pnl != null ? fmtInr(h.unrealized_pnl) : "—"}
                </td>
                <td className={`p-3 text-right font-mono tabular font-semibold ${pnlClass(h.change_pct)}`}>
                  {h.change_pct != null ? fmtPct(h.change_pct) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground sm:hidden">
        Sorted by {activeSortLabel || "Cur. value"}
      </div>
    </div>
  );
}
